import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiFolder, FiLayers, FiGrid, FiSettings, FiArrowRight,
  FiCheckCircle, FiCircle, FiRefreshCw, FiZap, FiCloud,
  FiDownload, FiUsers, FiPlay, FiPackage,
} from 'react-icons/fi';
import DashboardLayout from '../Dashboard/DashboardLayout';
import { getProjects } from '../../api/projects';
import { getConnections } from '../../api/connections';
import { getDevOpsConfig } from '../../api/devops';
import { listExecutions } from '../../api/executions';

type CheckStatus = 'loading' | 'done' | 'pending';

interface ChecklistItem {
  id: string;
  label: string;
  sublabel: string;
  status: CheckStatus;
  action: string;
  path: string;
}

const STEPS = [
  {
    icon: <FiFolder size={28} />,
    color: '#3B82F6',
    bg: '#EFF6FF',
    titleKey: 'steps.step1.title',
    descKey: 'steps.step1.description',
    actionKey: 'steps.step1.action',
    path: '/projects',
  },
  {
    icon: <FiLayers size={28} />,
    color: '#10B981',
    bg: '#F0FDF4',
    titleKey: 'steps.step2.title',
    descKey: 'steps.step2.description',
    actionKey: 'steps.step2.action',
    path: '/projects',
  },
  {
    icon: <FiGrid size={28} />,
    color: '#8B5CF6',
    bg: '#F5F3FF',
    titleKey: 'steps.step3.title',
    descKey: 'steps.step3.description',
    actionKey: 'steps.step3.action',
    path: '/templates',
  },
  {
    icon: <FiSettings size={28} />,
    color: '#F59E0B',
    bg: '#FFFBEB',
    titleKey: 'steps.step4.title',
    descKey: 'steps.step4.description',
    actionKey: 'steps.step4.action',
    path: '/settings/connections',
  },
];

const GettingStartedPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('gettingStarted');

  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    {
      id: 'project',
      label: t('setup.project.label'),
      sublabel: t('setup.project.sublabel'),
      status: 'loading',
      action: t('setup.project.action'),
      path: '/projects',
    },
    {
      id: 'connection',
      label: t('setup.connection.label'),
      sublabel: t('setup.connection.sublabel'),
      status: 'loading',
      action: t('setup.connection.action'),
      path: '/settings/connections',
    },
    {
      id: 'devops',
      label: t('setup.devops.label'),
      sublabel: t('setup.devops.sublabel'),
      status: 'loading',
      action: t('setup.devops.action'),
      path: '/settings',
    },
    {
      id: 'execution',
      label: t('setup.execution.label'),
      sublabel: t('setup.execution.sublabel'),
      status: 'loading',
      action: t('setup.execution.action'),
      path: '/executions/new',
    },
  ]);

  useEffect(() => {
    const runChecks = async () => {
      const [projectsRes, connectionsRes, devopsRes, executionsRes] = await Promise.allSettled([
        getProjects(),
        getConnections(),
        getDevOpsConfig(),
        listExecutions({ limit: 1 }),
      ]);

      setChecklist(prev =>
        prev.map(item => {
          if (item.id === 'project') {
            const ok = projectsRes.status === 'fulfilled' && projectsRes.value.length > 0;
            return { ...item, status: ok ? 'done' : 'pending' };
          }
          if (item.id === 'connection') {
            const ok = connectionsRes.status === 'fulfilled' && connectionsRes.value.length > 0;
            return { ...item, status: ok ? 'done' : 'pending' };
          }
          if (item.id === 'devops') {
            const ok = devopsRes.status === 'fulfilled' && devopsRes.value.configured;
            return { ...item, status: ok ? 'done' : 'pending' };
          }
          if (item.id === 'execution') {
            const ok = executionsRes.status === 'fulfilled' && executionsRes.value.total > 0;
            return { ...item, status: ok ? 'done' : 'pending' };
          }
          return item;
        })
      );
    };

    runChecks();
  }, []);

  const doneCount = checklist.filter(c => c.status === 'done').length;
  const totalCount = checklist.length;
  const progressPercent = (doneCount / totalCount) * 100;
  const allDone = doneCount === totalCount;

  const features = [
    { icon: <FiFolder size={18} />, label: t('features.list.projectManagement'), color: '#3B82F6' },
    { icon: <FiPackage size={18} />, label: t('features.list.visualEditor'), color: '#10B981' },
    { icon: <FiGrid size={18} />, label: t('features.list.templates'), color: '#8B5CF6' },
    { icon: <FiCloud size={18} />, label: t('features.list.multiCloud'), color: '#F59E0B' },
    { icon: <FiDownload size={18} />, label: t('features.list.exportImport'), color: '#EF4444' },
    { icon: <FiUsers size={18} />, label: t('features.list.collaboration'), color: '#6366F1' },
  ];

  return (
    <DashboardLayout>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>

        {/* ── Hero ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #6d28d9 100%)',
            borderRadius: '1.25rem',
            padding: '3rem 2.5rem',
            color: 'white',
            textAlign: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              background: 'rgba(255,255,255,0.15)',
              padding: '0.35rem 1rem',
              borderRadius: '9999px',
              fontSize: '0.8rem',
              fontWeight: 600,
              marginBottom: '1rem',
            }}
          >
            <FiZap size={13} />
            Onboarding
          </div>

          <h1 style={{ fontSize: '2.4rem', fontWeight: 800, margin: '0 0 0.75rem' }}>
            {t('header.title')}
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto 1.75rem' }}>
            {t('header.subtitle')}
          </p>

          {/* Progress bar */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'rgba(255,255,255,0.12)',
              borderRadius: '0.75rem',
              padding: '0.6rem 1.25rem',
            }}
          >
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
              {t('setup.progress')}
            </span>
            <div
              style={{
                width: '200px',
                height: '6px',
                background: 'rgba(255,255,255,0.25)',
                borderRadius: '9999px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progressPercent}%`,
                  background: 'white',
                  borderRadius: '9999px',
                  transition: 'width 0.8s ease',
                }}
              />
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>
              {doneCount}/{totalCount}
            </span>
          </div>
        </div>

        {/* ── Main grid : steps (gauche) + checklist (droite) ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 320px',
            gap: '1.5rem',
            marginBottom: '1.5rem',
          }}
        >
          {/* Steps — colonne gauche */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {STEPS.map((step, index) => (
              <div
                key={index}
                onClick={() => navigate(step.path)}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  padding: '1.5rem 1.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                }}
              >
                {/* Numéro */}
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: step.bg,
                    color: step.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </div>

                {/* Icône */}
                <div
                  style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '1rem',
                    background: step.bg,
                    color: step.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {step.icon}
                </div>

                {/* Texte */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: '0 0 0.3rem' }}>
                    {t(step.titleKey)}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
                    {t(step.descKey)}
                  </p>
                </div>

                {/* Bouton action */}
                <button
                  onClick={e => { e.stopPropagation(); navigate(step.path); }}
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: step.color,
                    background: step.bg,
                    border: `1.5px solid ${step.color}30`,
                    borderRadius: '0.6rem',
                    padding: '0.55rem 1.1rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s',
                  }}
                >
                  {t(step.actionKey)}
                  <FiArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Setup checklist — colonne droite (sticky) */}
          <div
            style={{
              background: 'white',
              borderRadius: '1rem',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              padding: '1.5rem',
              alignSelf: 'start',
              position: 'sticky',
              top: '1rem',
            }}
          >
            <h2
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#111827',
                margin: '0 0 1.25rem',
              }}
            >
              <FiPlay size={16} color="#3B82F6" />
              {t('setup.title')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              {checklist.map((item, idx) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  {item.status === 'loading' ? (
                    <FiRefreshCw
                      size={18}
                      color="#9CA3AF"
                      style={{ marginTop: '2px', flexShrink: 0, animation: 'spin 1s linear infinite' }}
                    />
                  ) : item.status === 'done' ? (
                    <FiCheckCircle size={18} color="#10B981" style={{ marginTop: '2px', flexShrink: 0 }} />
                  ) : (
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: '2px solid #D1D5DB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '2px',
                        flexShrink: 0,
                        fontSize: '0.65rem',
                        color: '#9CA3AF',
                        fontWeight: 700,
                      }}
                    >
                      {idx + 1}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: '0.83rem',
                        fontWeight: 600,
                        color: item.status === 'done' ? '#9CA3AF' : '#1F2937',
                        textDecoration: item.status === 'done' ? 'line-through' : 'none',
                        margin: 0,
                      }}
                    >
                      {item.label}
                    </p>
                    <p style={{ fontSize: '0.73rem', color: '#9CA3AF', margin: '2px 0 0', lineHeight: 1.4 }}>
                      {item.sublabel}
                    </p>
                    {item.status === 'pending' && (
                      <button
                        onClick={() => navigate(item.path)}
                        style={{
                          marginTop: '5px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          fontSize: '0.73rem',
                          fontWeight: 600,
                          color: '#3B82F6',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        {item.action} <FiArrowRight size={10} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {allDone && (
              <div
                style={{
                  marginTop: '1.25rem',
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '0.6rem',
                  padding: '0.875rem',
                  textAlign: 'center',
                }}
              >
                <FiCheckCircle size={20} color="#10B981" style={{ margin: '0 auto 6px', display: 'block' }} />
                <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#059669', margin: 0 }}>
                  {t('setup.allDone')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Features ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #faf5ff 100%)',
            borderRadius: '1.25rem',
            padding: '2rem 2.5rem',
            marginBottom: '1.5rem',
          }}
        >
          <h2
            style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#111827',
              textAlign: 'center',
              margin: '0 0 1.5rem',
            }}
          >
            {t('features.title')}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '0.875rem',
            }}
          >
            {features.map((f, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.6rem',
                  background: 'white',
                  borderRadius: '0.875rem',
                  padding: '1.25rem 0.75rem',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(0,0,0,0.04)',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '0.6rem',
                    background: `${f.color}18`,
                    color: f.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {f.icon}
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', lineHeight: 1.3 }}>{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div style={{ textAlign: 'center', paddingBottom: '0.5rem' }}>
          <button
            onClick={() => navigate('/projects')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              background: 'linear-gradient(135deg, #1e40af 0%, #6d28d9 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.875rem',
              padding: '0.9rem 2.25rem',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(30,64,175,0.35)',
              transition: 'transform 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.03)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            <FiZap size={18} />
            {t('cta.button')}
          </button>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </DashboardLayout>
  );
};

export default GettingStartedPage;
